import type { ComponentType } from 'react'

// Which files are pages, and how one is loaded by the name Rails gives it.
//
// It was `pages: '../pages'` in the entrypoint, which the Inertia Vite plugin
// expands to a glob of every `.tsx` under the directory. Every `.tsx` includes
// the `*.test.tsx` that sit beside the pages they test: the production build
// carried six of them as lazy chunks and, behind those, the whole Vitest
// runtime — about half a megabyte nobody could ever request, shipped and
// fingerprinted on every deploy, and one typo in a component name away from
// being served as a page. The plugin's `pages` option takes a directory and an
// extension and nothing to leave out, so the glob is written here instead, with
// the one negative pattern.
//
// Lazy, as the plugin's was: a page's module — and for the map pages the whole
// atlas layout behind it — is requested when Rails names that page and not
// before.
//
// ssr/ssr.tsx keeps a glob of its own with the SAME two patterns. It has to be
// eager there (a server render cannot await a chunk), `import.meta.glob` takes
// only literals, and so the pair cannot be shared as a value. Change both.
type PageModule = { default: ComponentType }

export const pageLoaders = import.meta.glob<PageModule>(['../pages/**/*.tsx', '!../pages/**/*.test.tsx'])

/**
 * The component for the snake_case "controller/action" identifier Rails passes
 * to `render inertia:`. The default export itself, with whatever static
 * `layout` it carries — that property is how the map pages share one persistent
 * shell.
 */
export async function resolvePage(name: string): Promise<ComponentType> {
  const load = pageLoaders[`../pages/${name}.tsx`]
  if (!load) throw new Error(`Page not found: ${name}`)

  return (await load()).default
}

/**
 * Asks for pages' code before anything names them, so the visit that does
 * finds it already loaded.
 *
 * A page is a chunk of its own, requested when Rails first renders it. For the
 * three panels that open over the map that was one more round trip on the
 * first place, the first producer and the first grape a reader opened — after
 * the click, in front of the content, and not something fetching the page's
 * DATA ahead of time could remove. The shell calls this once the map has
 * drawn and the browser is idle.
 *
 * Quiet on every count: a name that is not a page is skipped, and a chunk that
 * fails to load here is simply asked for again by the visit that needs it,
 * which is where a failure belongs.
 */
export function warmPages(names: readonly string[]): void {
  for (const name of names) {
    pageLoaders[`../pages/${name}.tsx`]?.().catch(() => {})
  }
}
